// Unified data layer for Enrollment Pipeline.
// Live Supabase when configured; localStorage demo otherwise.

import { supabase, isSupabaseConfigured } from "./supabase";
import {
  demoAddNote,
  demoCreateInquiry,
  demoGetRecord,
  demoListRecords,
  demoUpdateRecord,
} from "./enrollmentDemo";

const RECORD_SELECT = `
  id, organization_id, parent_first_name, parent_last_name, parent_email,
  parent_phone, child_first_name, child_last_name, child_date_of_birth,
  location_id, preferred_classroom_id, preferred_age_group, preferred_start_date,
  desired_program, referral_name, sibling_child_id, waitlist_priority,
  waitlist_entered_at, offer_expires_at, archived_at,
  parent_notes, source, stage, stage_changed_at,
  submitted_by_profile_id, linked_family_id, linked_child_id,
  created_at, updated_at,
  classroom:classrooms(id, name, age_group),
  location:locations(id, name)
`;

export async function listEnrollmentRecords({ role, profileId, filters = {} }) {
  if (!isSupabaseConfigured) {
    return demoListRecords({ role, profileId, filters });
  }
  let query = supabase
    .from("enrollment_records")
    .select(RECORD_SELECT)
    .order("stage_changed_at", { ascending: false });
  if (filters.stage)       query = query.eq("stage", filters.stage);
  if (filters.classroomId) query = query.eq("preferred_classroom_id", filters.classroomId);
  if (filters.source)      query = query.eq("source", filters.source);
  if (filters.search) {
    const q = filters.search.replace(/%/g, "");
    query = query.or(
      `parent_first_name.ilike.%${q}%,parent_last_name.ilike.%${q}%,parent_email.ilike.%${q}%,child_first_name.ilike.%${q}%,child_last_name.ilike.%${q}%`,
    );
  }
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function getEnrollmentRecord(id) {
  if (!isSupabaseConfigured) return demoGetRecord(id);
  const [recordRes, notesRes, historyRes] = await Promise.all([
    supabase.from("enrollment_records").select(RECORD_SELECT).eq("id", id).maybeSingle(),
    supabase
      .from("enrollment_notes")
      .select("id, body, author_id, created_at, author:profiles!enrollment_notes_author_id_fkey(id, full_name, email)")
      .eq("enrollment_id", id)
      .order("created_at", { ascending: false }),
    supabase
      .from("enrollment_stage_history")
      .select("id, from_stage, to_stage, note, created_at, changed_by")
      .eq("enrollment_id", id)
      .order("created_at", { ascending: true }),
  ]);
  if (recordRes.error) throw recordRes.error;
  if (!recordRes.data) return null;
  return {
    ...recordRes.data,
    notes: notesRes.data || [],
    history: (historyRes.data || []).map((h) => ({
      from_stage: h.from_stage,
      to_stage: h.to_stage,
      note: h.note,
      changed_at: h.created_at,
      changed_by: h.changed_by,
    })),
  };
}

export async function submitInquiry(payload) {
  if (!isSupabaseConfigured) return demoCreateInquiry(payload);
  // Live mode: parents POST via the SECURITY-DEFINER RPC so stage /
  // organization_id are server-controlled.
  const { data, error } = await supabase.rpc("rpc_submit_enrollment_inquiry", {
    p_parent_first_name: payload.parentFirstName,
    p_parent_last_name: payload.parentLastName || null,
    p_parent_email: payload.parentEmail,
    p_parent_phone: payload.parentPhone || null,
    p_child_first_name: payload.childFirstName || null,
    p_child_last_name: payload.childLastName || null,
    p_child_dob: payload.childDob || null,
    p_preferred_classroom: payload.preferredClassroomId || null,
    p_preferred_age_group: payload.preferredAgeGroup || null,
    p_preferred_start_date: payload.preferredStartDate || null,
    p_notes: payload.parentNotes || null,
    p_source: payload.source || "website",
  });
  if (error) throw error;
  return data;
}

export async function updateEnrollmentStage({
  id,
  stage,
  profileId,
  expectedUpdatedAt = null,
  note = null,
  offerExpiresAt = null,
  waitlistPriority = null,
}) {
  if (!isSupabaseConfigured) {
    return demoUpdateRecord(id, { stage, changedBy: profileId });
  }
  const { data, error } = await supabase.rpc("rpc_transition_admission", {
    p_enrollment_id: id,
    p_to_stage: stage,
    p_expected_updated_at: expectedUpdatedAt,
    p_note: note || null,
    p_offer_expires_at: offerExpiresAt || null,
    p_waitlist_priority: Number.isFinite(waitlistPriority) ? waitlistPriority : null,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function updateEnrollmentRecord({ id, patch, expectedUpdatedAt = null }) {
  if (!isSupabaseConfigured) return demoUpdateRecord(id, patch);
  if (!expectedUpdatedAt) {
    throw new Error("This enrollment was updated elsewhere. Refresh before saving details.");
  }
  const { data, error } = await supabase.rpc("rpc_update_admission_details", {
    p_enrollment_id: id,
    p_expected_updated_at: expectedUpdatedAt,
    p_patch: patch,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

/**
 * Parent-only draft edits. The database enforces both ownership and the two
 * editable stages; this helper exists so a future parent editor cannot fall
 * back to a broad table update.
 */
export async function updateParentEnrollmentDraft({ id, patch, expectedUpdatedAt = null }) {
  if (!isSupabaseConfigured) return demoUpdateRecord(id, patch);
  const { data, error } = await supabase.rpc("rpc_update_parent_enrollment_draft", {
    p_enrollment_id: id,
    p_expected_updated_at: expectedUpdatedAt,
    p_patch: patch,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function addEnrollmentNote({ enrollmentId, authorId, body }) {
  if (!isSupabaseConfigured) {
    return demoAddNote({ enrollmentId, authorId, body });
  }
  const { data, error } = await supabase
    .from("enrollment_notes")
    .insert({ enrollment_id: enrollmentId, author_id: authorId, body })
    .select("id, body, author_id, created_at")
    .single();
  if (error) throw error;
  return data;
}

/**
 * Admin one-click convert-to-family. Calls the SECURITY DEFINER RPC which
 * guarantees idempotency (returns existing link if the record is already
 * converted) and writes all four rows transactionally.
 */
export async function convertEnrollmentToFamily(enrollmentId) {
  if (!isSupabaseConfigured) {
    throw new Error("Conversion is only available in live mode.");
  }
  const { data, error } = await supabase.rpc(
    "rpc_convert_enrollment_to_family",
    { p_enrollment_id: enrollmentId },
  );
  if (error) throw error;
  // RPC returns a single-row table; supabase-js surfaces it as an array.
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    family_id: row.out_family_id ?? row.family_id ?? null,
    child_id: row.out_child_id ?? row.child_id ?? null,
    created_new: row.out_created_new ?? row.created_new ?? false,
  };
}
